/**
 * outline-store — shared source of truth for the ChatOutline timeline rail.
 * ------------------------------------------------------------------------
 * Why this exists: the chat message list is virtualized with react-virtuoso
 * (added 2026-06-12), so off-screen messages are NOT in the DOM. ChatOutline
 * used to derive its turns from `document.querySelectorAll`, which after
 * virtualization only ever saw the few messages currently mounted near the
 * viewport — so the timeline appeared to "fill in" as you scrolled.
 *
 * Fix: the active Messages / ArtifactMessages component (which already has the
 * full `messages` array in memory) publishes the turns here, and registers a
 * jump function backed by Virtuoso's `scrollToIndex` so we can jump to a turn
 * even when its DOM node is currently unmounted. ChatOutline subscribes via
 * useSyncExternalStore. No DB, no endpoint, no extra fetch — just the data the
 * chat already holds.
 */

export interface OutlineTurn {
  id: string;
  /** Position in the messages array — used for Virtuoso scrollToIndex. */
  index: number;
  role: "user" | "assistant";
  label: string;
}

const MAX_LABEL_LENGTH = 90;

let turns: OutlineTurn[] = [];
let jumpFn: ((index: number) => void) | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function turnsEqual(a: OutlineTurn[], b: OutlineTurn[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (
      a[i].id !== b[i].id ||
      a[i].role !== b[i].role ||
      a[i].label !== b[i].label ||
      a[i].index !== b[i].index
    ) {
      return false;
    }
  }
  return true;
}

export function setOutlineTurns(next: OutlineTurn[]) {
  if (turnsEqual(turns, next)) return;
  turns = next;
  emit();
}

export function clearOutlineTurns() {
  if (turns.length === 0) return;
  turns = [];
  emit();
}

export function setOutlineJump(fn: ((index: number) => void) | null) {
  jumpFn = fn;
}

export function jumpToOutlineIndex(index: number) {
  jumpFn?.(index);
}

export function subscribeOutline(callback: () => void) {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

export function getOutlineTurns() {
  return turns;
}

// Stable empty reference for SSR snapshot (useSyncExternalStore requirement).
const EMPTY_TURNS: OutlineTurn[] = [];
export function getOutlineServerSnapshot() {
  return EMPTY_TURNS;
}

// Strip trailing timestamps like "21/05/2026, 07:55" that some message text
// can carry, matching the previous DOM-based behavior.
const TIMESTAMP_TAIL_RE =
  /\s*\d{1,2}\/\d{1,2}\/\d{4},?\s*\d{1,2}:\d{2}(?::\d{2})?\s*$/;

function extractMessageLabel(
  message: { parts?: unknown; content?: unknown },
  role: "user" | "assistant",
): string {
  let text = "";
  const parts = Array.isArray(message.parts) ? message.parts : [];
  for (const part of parts) {
    if (
      part &&
      typeof part === "object" &&
      (part as { type?: unknown }).type === "text" &&
      typeof (part as { text?: unknown }).text === "string"
    ) {
      const chunk = (part as { text: string }).text;
      text += text ? ` ${chunk}` : chunk;
    }
  }
  if (!text && typeof message.content === "string") {
    text = message.content;
  }
  text = text.replace(/\s+/g, " ").trim();
  text = text.replace(TIMESTAMP_TAIL_RE, "").trim();

  const fallback = role === "assistant" ? "Xynth response" : "Your prompt";
  const label = text.length > 0 ? text : fallback;
  return label.length > MAX_LABEL_LENGTH
    ? `${label.slice(0, MAX_LABEL_LENGTH - 1)}…`
    : label;
}

/** Build the timeline turns from the in-memory chat messages. */
export function deriveOutlineTurns(
  messages: Array<{ id: string; role: string; parts?: unknown; content?: unknown }>,
): OutlineTurn[] {
  const out: OutlineTurn[] = [];
  messages.forEach((message, i) => {
    const role = message.role;
    if (role !== "user" && role !== "assistant") return;
    if (!message.id) return;
    out.push({
      id: message.id,
      index: i,
      role,
      label: extractMessageLabel(message, role),
    });
  });
  return out;
}
